import { useCallback, useRef, useState } from "react";
import { Camera, ClipboardCheck, FileDown, Plus, Share2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, StatusBadge, LoadingState, ErrorState, Field } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, downloadFile, fileUrl } from "@/lib/api";
import { t } from "@/i18n";
import { SecureLinkButton } from "@/modules/secure_links/SecureLinkButton";

const STATE_ON = { ok: "border-emerald-600 bg-emerald-600 text-white", good: "border-emerald-600 bg-emerald-600 text-white", attention: "border-amber-500 bg-amber-500 text-white",
  monitor: "border-amber-500 bg-amber-500 text-white", urgent: "border-red-600 bg-red-600 text-white", na: "border-slate-500 bg-slate-500 text-white" };
const stateLabel = (s) => (["good", "monitor", "na"].includes(s) ? t(`b10.insp.states.${s}`) : t(`b9.diag.states.${s}`));

export const rangeText = (it) => {
  const u = it.unit || "";
  if (it.min != null && it.max != null) return `${it.min}–${it.max} ${u}`.trim();
  if (it.min != null) return `≥ ${it.min} ${u}`.trim();
  if (it.max != null) return `≤ ${it.max} ${u}`.trim();
  return "";
};

function Counts({ c }) {
  const [first, second] = [c.ok ?? c.good ?? 0, c.attention ?? c.monitor ?? 0];
  return <span className="flex gap-1.5 text-[11px] font-semibold"><span className="text-emerald-700">{first} OK</span>·<span className="text-amber-700">{second}</span>·<span className="text-red-700">{c.urgent}</span></span>;
}

function Blur({ value, onSave, area, ...props }) {
  const [v, setV] = useState(value || "");
  const C = area ? Textarea : Input;
  return <C value={v} onChange={(e) => setV(e.target.value)} onBlur={() => (value || "") !== v && onSave(v)} {...props} />;
}

function ItemPhotos({ d, it, locked, onUpdated }) {
  const ref = useRef(null);
  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    api.post(`/diagnostics/${d.id}/items/${it.id}/photos`, fd).then((r) => onUpdated(r.data)).catch((err) => toast.error(errMsg(err)));
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`diag-photos-${it.id}`}>
      {(it.photos || []).map((p) => (
        <span key={p.id} className="relative">
          <a href={fileUrl(p.url)} target="_blank" rel="noreferrer"><img src={fileUrl(p.url)} alt="" className="h-14 w-14 rounded-md object-cover" /></a>
          {!locked && <button type="button" className="absolute -right-1 -top-1 rounded-full bg-white p-0.5 shadow" onClick={() => api.delete(`/diagnostics/${d.id}/items/${it.id}/photos/${p.id}`).then((r) => onUpdated(r.data))} aria-label={t("common.remove")}><X className="h-3 w-3" /></button>}
        </span>
      ))}
      {!locked && <>
        <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid={`diag-photo-input-${it.id}`} />
        <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} data-testid={`diag-photo-add-${it.id}`}><Camera className="mr-1 h-4 w-4" />{t("b10.insp.addPhoto")}</Button>
      </>}
    </div>
  );
}

function ItemRow({ d, it, locked, onPatch, onUpdated }) {
  const [val, setVal] = useState(it.value ?? "");
  const [more, setMore] = useState(Boolean(it.action || it.internal_note || it.photos?.length));
  const insp = d.scale === "inspection";
  const numeric = insp || it.unit || it.min != null || it.max != null;
  const range = rangeText(it);
  return (
    <div className="space-y-2 border-b border-slate-100 py-3" data-testid={`diag-item-${it.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{it.label} {!it.state && <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500" data-testid={`diag-not-checked-${it.id}`}>{t("b10.insp.notChecked")}</span>}</p>
          {range && <p className="text-xs text-slate-500">{t("b9.diag.expected", { range })}</p>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {d.states.map((s) => (
            <button key={s} type="button" disabled={locked} onClick={() => onPatch({ state: it.state === s ? null : s })} data-testid={`diag-state-${it.id}-${s}`}
              className={`min-h-[40px] min-w-[64px] rounded-lg border px-2.5 text-xs font-bold transition-colors duration-150 disabled:opacity-60 ${it.state === s ? STATE_ON[s] : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{stateLabel(s)}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {numeric && (
          <div className="flex items-center gap-1.5">
            <Input type="number" step="any" inputMode="decimal" disabled={locked} value={val} placeholder={t("b9.diag.reading")} className="h-10 w-28"
              onChange={(e) => setVal(e.target.value)} onBlur={() => String(it.value ?? "") !== String(val) && onPatch({ value: val === "" ? null : Number(val) })} data-testid={`diag-value-${it.id}`} />
            {insp ? <Blur disabled={locked} value={it.unit} placeholder={t("b10.insp.unit")} className="h-10 w-20" onSave={(v) => onPatch({ unit: v })} data-testid={`diag-unit-${it.id}`} />
              : it.unit && <span className="text-xs text-slate-500">{it.unit}</span>}
          </div>
        )}
        <Blur disabled={locked} value={it.note} placeholder={insp ? t("b10.insp.observation") : t("b9.diag.note")} className="h-10 min-w-[160px] flex-1" onSave={(v) => onPatch({ note: v })} data-testid={`diag-note-${it.id}`} />
        <Button type="button" variant="ghost" size="sm" onClick={() => setMore(!more)} data-testid={`diag-more-${it.id}`}>{more ? "−" : "+"}</Button>
      </div>
      {more && (
        <div className="space-y-2 rounded-md bg-slate-50 p-2">
          <Blur disabled={locked} value={it.action} placeholder={t("b10.insp.action")} onSave={(v) => onPatch({ action: v })} data-testid={`diag-action-${it.id}`} />
          <Blur disabled={locked} value={it.internal_note} placeholder={t("b10.insp.internal")} className="border-dashed" onSave={(v) => onPatch({ internal_note: v })} data-testid={`diag-internal-${it.id}`} />
          <ItemPhotos d={d} it={it} locked={locked} onUpdated={onUpdated} />
        </div>
      )}
    </div>
  );
}

function Priority({ d }) {
  const second = d.scale === "inspection" ? "monitor" : "attention";
  const rows = ["urgent", second].flatMap((s) => d.items.filter((i) => i.state === s).map((i) => ({ ...i, s })));
  return (
    <div className="space-y-1 pt-4" data-testid="diag-priority-summary">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700">{t("b10.insp.priority")}</p>
      {rows.length === 0 ? <p className="text-xs text-slate-500">{t("b10.insp.noPriority")}</p> : rows.map((i) => (
        <p key={i.id} className="text-sm"><span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${STATE_ON[i.s]}`}>{stateLabel(i.s)}</span>{i.label}{(i.action || i.note) && <span className="text-slate-500"> — {i.action || i.note}</span>}</p>
      ))}
    </div>
  );
}

function DiagnosticDialog({ diag, onClose, onChange, readOnly }) {
  const [d, setD] = useState(diag);
  const [warn, setWarn] = useState(false);
  const set = (x) => { setD(x); onChange(); };
  const patch = (it) => (body) => api.patch(`/diagnostics/${d.id}/items/${it.id}`, body).then((r) => set(r.data)).catch((e) => toast.error(errMsg(e)));
  const patchDiag = (body) => api.patch(`/diagnostics/${d.id}`, body).then((r) => set(r.data)).catch((e) => toast.error(errMsg(e)));
  const action = (a, msg) => api.post(`/diagnostics/${d.id}/${a}`).then((r) => { set(r.data); setWarn(false); if (msg) toast.success(msg); }).catch((e) => toast.error(errMsg(e)));
  const locked = readOnly || d.status === "completed";
  const sections = [...new Set(d.items.map((i) => i.section || ""))];
  const needsValidation = !d.validated_at;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[100dvh] max-w-3xl flex-col gap-0 p-0 sm:h-[92vh]" data-testid="diagnostic-dialog">
        <DialogHeader className="border-b border-slate-200 p-4 text-left">
          <DialogTitle className="flex flex-wrap items-center gap-2">{d.name} <StatusBadge status={d.status} /></DialogTitle>
          <DialogDescription className="flex items-center gap-3">{t("b9.diag.progress", { checked: d.checked, total: d.total })} <Counts c={d.counts} /></DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-4">
          {d.header_fields.length > 0 && (
            <div className="pt-3" data-testid="diag-header-fields">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700">{t("b10.insp.header")}</p>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {d.header_fields.map((f) => <Field key={f.key} label={f.label}><Blur disabled={locked} value={d.header[f.key]} onSave={(v) => patchDiag({ header: { [f.key]: v } })} data-testid={`diag-header-${f.key}`} /></Field>)}
              </div>
            </div>
          )}
          {sections.map((sec) => (
            <div key={sec} className="pt-3">
              {sec && <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700">{sec}</p>}
              {d.items.filter((i) => (i.section || "") === sec).map((it) => <ItemRow key={it.id} d={d} it={it} locked={locked} onPatch={patch(it)} onUpdated={set} />)}
            </div>
          ))}
          <div className="space-y-3 pt-4" data-testid="diag-scan">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700">{t("b14.scan")}</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label={t("b14.scanDate")}><Blur disabled={locked} value={d.diagnostic_date} placeholder="dd/mm/yyyy" onSave={(v) => patchDiag({ diagnostic_date: v })} data-testid="diag-scan-date" /></Field>
              <Field label={t("b14.scanTime")}><Blur disabled={locked} value={d.diagnostic_time} placeholder="HH:MM" onSave={(v) => patchDiag({ diagnostic_time: v })} data-testid="diag-scan-time" /></Field>
            </div>
            <Field label={t("b14.scanReport")}><Blur area disabled={locked} value={d.diagnostic_report} onSave={(v) => patchDiag({ diagnostic_report: v })} data-testid="diag-scan-report" /></Field>
          </div>
          <Priority d={d} />
          <div className="space-y-3 py-4">
            <Field label={t("b10.insp.recommendations")}><Blur area disabled={locked} value={d.recommendations} onSave={(v) => patchDiag({ recommendations: v })} data-testid="diag-recommendations" /></Field>
            <Field label={t("b10.insp.customerObs")}><Blur area disabled={locked} value={d.customer_observations} onSave={(v) => patchDiag({ customer_observations: v })} data-testid="diag-customer-observations" /></Field>
            <Field label={t("b10.insp.internalNotes")}><Blur area disabled={locked} value={d.internal_notes} className="border-dashed" onSave={(v) => patchDiag({ internal_notes: v })} data-testid="diag-internal-notes" /></Field>
          </div>
        </div>
        {warn && (
          <div className="max-h-40 overflow-y-auto border-t border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" data-testid="diag-not-checked-warning">
            <p className="font-semibold">{t("b10.insp.warnTitle", { n: d.not_checked.length })}</p>
            <p className="text-xs">{t("b10.insp.warnText")}</p>
            <p className="mt-1 text-xs">{d.not_checked.join(" · ")}</p>
            <Button size="sm" className="mt-2" onClick={() => action("complete")} data-testid="diag-complete-anyway">{t("b10.insp.completeAnyway")}</Button>
          </div>
        )}
        {d.status === "completed" && !readOnly && (
          <div className="border-t border-slate-200 px-3 pt-3 text-sm">
            {d.validated_at ? <p className="text-emerald-700" data-testid="diag-validated">{t("b10.insp.validated", { who: d.validated_by_name })}</p>
              : <label className="flex items-center gap-2 font-medium"><input type="checkbox" className="h-5 w-5" onChange={(e) => e.target.checked && action("validate")} data-testid="diag-validate-checkbox" />{t("b10.insp.validateLabel")}</label>}
          </div>
        )}
        <div className="flex flex-wrap gap-2 border-t border-slate-200 p-3">
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/diagnostics/${d.id}/report.pdf`)} data-testid="diag-pdf"><FileDown className="mr-1.5 h-4 w-4" />{d.validated_at ? t("b14.pdf") : t("b14.draftPdf")}</Button>
          {!readOnly && (d.status === "completed"
            ? <>
              <Button variant="outline" size="sm" onClick={() => action("reopen")} data-testid="diag-reopen">{t("b9.diag.reopen")}</Button>
              <Button size="sm" disabled={needsValidation} title={needsValidation ? t("b10.insp.validateFirst") : ""} onClick={() => action("share", t("b9.diag.shared"))} data-testid="diag-share"><Share2 className="mr-1.5 h-4 w-4" />{t("b9.diag.share")}</Button>
              <SecureLinkButton kind="report" refId={d.id} label={t("b15.send")} disabled={needsValidation} title={needsValidation ? t("b10.insp.validateFirst") : ""} onDone={onChange} />
            </>
            : <Button size="sm" onClick={() => (d.not_checked.length ? setWarn(true) : action("complete"))} data-testid="diag-complete">{t("b9.diag.complete")}</Button>)}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose} data-testid="diag-close">{t("b9.diag.close")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DiagnosticsCard({ job, readOnly }) {
  const { data, reload } = useFetch(`/jobs/${job.id}/diagnostics`);
  const templates = useFetch(readOnly ? null : "/diagnostics/templates");
  const [tpl, setTpl] = useState("");
  const [open, setOpen] = useState(null);
  const start = () => api.post(`/jobs/${job.id}/diagnostics`, { template_id: tpl }).then((r) => { reload(); setOpen(r.data); setTpl(""); }).catch((e) => toast.error(errMsg(e)));
  const onChange = useCallback(() => reload(), [reload]);
  return (
    <Card testId="job-diagnostics-card">
      <SectionTitle hint={t("b14.hint")}>{t("b14.title")}</SectionTitle>
      {data && data.length === 0 && <p className="mb-3 text-sm text-slate-500">{t("b9.diag.none")}</p>}
      <div className="space-y-2">
        {(data || []).map((d) => (
          <button key={d.id} type="button" onClick={() => setOpen(d)} data-testid={`diag-row-${d.id}`}
            className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors duration-150 hover:bg-slate-50">
            <span className="flex items-center gap-2 text-sm font-semibold"><ClipboardCheck className="h-4 w-4 text-teal-700" />{d.name}</span>
            <span className="flex items-center gap-2"><Counts c={d.counts} />{d.shared_at ? <StatusBadge status="sent" /> : <StatusBadge status={d.status} />}</span>
          </button>
        ))}
      </div>
      {!readOnly && !["completed", "cancelled"].includes(job.status) && (
        <div className="mt-4 flex gap-2">
          <Select value={tpl} onValueChange={setTpl}>
            <SelectTrigger className="flex-1" data-testid="diag-template-select"><SelectValue placeholder={t("b9.diag.pickTemplate")} /></SelectTrigger>
            <SelectContent>{(templates.data || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button disabled={!tpl} onClick={start} data-testid="diag-start"><Plus className="mr-1 h-4 w-4" />{t("b9.diag.start")}</Button>
        </div>
      )}
      {open && <DiagnosticDialog key={open.id} diag={open} readOnly={readOnly} onClose={() => { setOpen(null); reload(); }} onChange={onChange} />}
    </Card>
  );
}

const blankItem = () => ({ section: "", label: "", unit: "", min: "", max: "" });

function TemplateEditor({ tpl, onSaved, onCancel }) {
  const [name, setName] = useState(tpl?.name || "");
  const [items, setItems] = useState(tpl ? tpl.items.map((i) => ({ ...i, unit: i.unit || "", min: i.min ?? "", max: i.max ?? "" })) : [blankItem()]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const save = async () => {
    setBusy(true);
    const payload = { name, scale: tpl?.scale || "standard", header_fields: tpl?.header_fields || [], require_validation: !!tpl?.require_validation, footer_text: tpl?.footer_text || "", items: items.filter((i) => i.label.trim()).map((i) => ({ id: i.id, section: i.section, label: i.label, unit: i.unit || null,
      min: i.min === "" ? null : Number(i.min), max: i.max === "" ? null : Number(i.max) })) };
    try {
      if (tpl) await api.put(`/diagnostics/templates/${tpl.id}`, payload);
      else await api.post("/diagnostics/templates", payload);
      toast.success(t("common.saved"));
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-4" data-testid="diag-template-editor">
      <Field label={t("b9.diag.name")}><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="diag-template-name" /></Field>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 rounded-md bg-slate-50 p-2 sm:grid-cols-[1fr_1.6fr_70px_70px_70px_auto]" data-testid={`diag-template-item-${i}`}>
            <Input placeholder={t("b9.diag.section")} value={it.section} onChange={(e) => set(i, { section: e.target.value })} />
            <Input placeholder={t("b9.diag.label")} value={it.label} onChange={(e) => set(i, { label: e.target.value })} data-testid={`diag-template-label-${i}`} />
            <Input placeholder={t("b9.diag.unit")} value={it.unit} onChange={(e) => set(i, { unit: e.target.value })} />
            <Input type="number" step="any" placeholder={t("b9.diag.min")} value={it.min} onChange={(e) => set(i, { min: e.target.value })} data-testid={`diag-template-min-${i}`} />
            <Input type="number" step="any" placeholder={t("b9.diag.max")} value={it.max} onChange={(e) => set(i, { max: e.target.value })} />
            <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label={t("common.remove")}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setItems([...items, blankItem()])} data-testid="diag-template-add-item"><Plus className="mr-1 h-4 w-4" />{t("b9.diag.addItem")}</Button>
        <Button disabled={busy || name.trim().length < 2} onClick={save} data-testid="diag-template-save">{t("common.save")}</Button>
        <Button variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button>
      </div>
    </div>
  );
}

export function DiagnosticTemplates() {
  const { canManage } = useGarage();
  const { data, error, loading, reload } = useFetch("/diagnostics/templates");
  const [edit, setEdit] = useState(null);
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const done = () => { setEdit(null); reload(); };
  return (
    <div className="space-y-3" data-testid="diag-templates">
      {data.map((tp) => edit?.id === tp.id ? <TemplateEditor key={tp.id} tpl={tp} onSaved={done} onCancel={() => setEdit(null)} /> : (
        <div key={tp.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3" data-testid={`diag-template-${tp.id}`}>
          <div><p className="font-semibold">{tp.name}</p><p className="text-xs text-slate-500">{tp.items.length} items</p></div>
          {canManage && <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setEdit(tp)} data-testid={`diag-template-edit-${tp.id}`}>{t("common.edit")}</Button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => api.delete(`/diagnostics/templates/${tp.id}`).then(reload)} data-testid={`diag-template-delete-${tp.id}`}>{t("b9.diag.deleteTemplate")}</Button>
          </div>}
        </div>
      ))}
      {canManage && (edit === "new" ? <TemplateEditor onSaved={done} onCancel={() => setEdit(null)} />
        : <Button onClick={() => setEdit("new")} data-testid="diag-template-new"><Plus className="mr-1 h-4 w-4" />{t("b9.diag.newTemplate")}</Button>)}
    </div>
  );
}
